import React, { useMemo } from 'react';
import {
  Search,
  Filter,
  Edit,
  Copy,
  CreditCard,
  Archive,
  Trash2,
  Receipt,
  ChevronDown,
  ChevronRight,
  FileText,
  Plus,
  Building2,
  Clock3,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';

const FinancialInvoicesWorkspace = ({
  reportNavigationLabel,
  reportInvoiceFilter,
  statusFilter,
  setStatusFilter,
  externalPartnerFilter,
  externalServiceFilter,
  clearReportNavigation,
  searchTerm,
  setSearchTerm,
  statusOptions,
  patientFilter,
  setPatientFilter,
  patients,
  filteredInvoices,
  invoices,
  formatDate,
  formatCurrency,
  patientWalletSummaries,
  getExternalServiceLabel,
  getStatusColor,
  handleEditExternalRevenue,
  handleEditInvoice,
  handleDuplicateExternalRevenue,
  handleAddPayment,
  handleDispenseNfse,
  handleDeleteInvoice,
  expandedInvoices,
  setExpandedInvoices,
  onNewInvoice,
  onNewExternalRevenue
}) => {
  const summary = useMemo(() => {
    const rows = Array.isArray(invoices) ? invoices : [];
    const openStatuses = new Set([
      'pendente',
      'parcialmente_pago',
      'vencido',
      'faturado',
      'parcialmente_recebido'
    ]);
    const paidStatuses = new Set(['pago', 'recebido']);

    return rows.reduce(
      (acc, invoice) => {
        const total = Number(invoice?.total_amount || invoice?.gross_amount || 0);
        const received = Number(invoice?.received_amount || 0);
        const balance = Math.max(total - received, 0);
        const status = invoice?.status;

        acc.total += total;

        if (openStatuses.has(status)) {
          acc.open += balance || total;
          acc.openCount += 1;
        }

        if (status === 'vencido') {
          acc.overdue += balance || total;
          acc.overdueCount += 1;
        }

        if (paidStatuses.has(status)) {
          acc.received += received > 0 ? received : total;
          acc.receivedCount += 1;
        } else {
          acc.received += received;
        }

        if (invoice?.invoice_type === 'non_patient') {
          acc.external += total;
          acc.externalCount += 1;
        }

        return acc;
      },
      {
        total: 0,
        open: 0,
        openCount: 0,
        overdue: 0,
        overdueCount: 0,
        received: 0,
        receivedCount: 0,
        external: 0,
        externalCount: 0
      }
    );
  }, [invoices]);

  const summaryCards = [
    {
      label: 'Em aberto',
      value: summary.open,
      detail: `${summary.openCount} lançamento(s)`,
      icon: Clock3,
      wrapper: 'border-amber-200 bg-amber-50',
      iconStyle: 'bg-amber-100 text-amber-700',
      valueStyle: 'text-amber-950'
    },
    {
      label: 'Recebido',
      value: summary.received,
      detail: `${summary.receivedCount} quitado(s)`,
      icon: CheckCircle2,
      wrapper: 'border-emerald-200 bg-emerald-50',
      iconStyle: 'bg-emerald-100 text-emerald-700',
      valueStyle: 'text-emerald-950'
    },
    {
      label: 'Vencido',
      value: summary.overdue,
      detail: `${summary.overdueCount} cobrança(s)`,
      icon: AlertTriangle,
      wrapper: 'border-red-200 bg-red-50',
      iconStyle: 'bg-red-100 text-red-700',
      valueStyle: 'text-red-950'
    },
    {
      label: 'Receita externa',
      value: summary.external,
      detail: `${summary.externalCount} lançamento(s)`,
      icon: Building2,
      wrapper: 'border-indigo-200 bg-indigo-50',
      iconStyle: 'bg-indigo-100 text-indigo-700',
      valueStyle: 'text-indigo-950'
    }
  ];

  const hasActiveFilters = Boolean(searchTerm || statusFilter || patientFilter);

  const clearWorkspaceFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPatientFilter('');
    clearReportNavigation();
  };

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-purple-50 px-5 py-5 sm:px-6">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex items-start gap-4">
              <div className="rounded-2xl bg-purple-600 p-3 text-white shadow-sm">
                <FileText className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-purple-600">
                  Centro de cobranças
                </p>
                <h3 className="mt-1 text-2xl font-bold text-slate-950">
                  Contas a receber e outras receitas
                </h3>
                <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
                  Acompanhe o que foi cobrado, o que já foi recebido e a origem de cada lançamento.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={onNewExternalRevenue}
                className="inline-flex min-h-[42px] items-center justify-center rounded-xl border border-indigo-200 bg-white px-4 py-2.5 text-sm font-semibold text-indigo-700 transition-colors hover:bg-indigo-50"
              >
                <Building2 className="mr-2 h-4 w-4" />
                Receita externa
              </button>
              <button
                type="button"
                onClick={onNewInvoice}
                className="inline-flex min-h-[42px] items-center justify-center rounded-xl bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-purple-700"
              >
                <Plus className="mr-2 h-4 w-4" />
                Nova conta a receber
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:p-5 xl:grid-cols-4">
          {summaryCards.map((card) => {
            const Icon = card.icon;

            return (
              <div
                key={card.label}
                className={`rounded-xl border p-4 ${card.wrapper}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                      {card.label}
                    </p>
                    <p className={`mt-2 truncate text-xl font-bold ${card.valueStyle}`}>
                      {formatCurrency(card.value)}
                    </p>
                    <p className="mt-1 text-xs text-slate-600">{card.detail}</p>
                  </div>
                  <div className={`rounded-xl p-2.5 ${card.iconStyle}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {reportNavigationLabel &&
        (reportInvoiceFilter ||
          statusFilter ||
          externalPartnerFilter ||
          externalServiceFilter) && (
          <div className="flex flex-col gap-3 rounded-xl border border-purple-200 bg-purple-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-purple-900">
                Filtro aberto pelo relatório
              </p>
              <p className="text-sm text-purple-700">{reportNavigationLabel}</p>
            </div>
            <button
              type="button"
              onClick={clearReportNavigation}
              className="inline-flex items-center justify-center rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-100"
            >
              Limpar filtro
            </button>
          </div>
        )}

      <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                Pesquisar
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Paciente, número, parceiro ou serviço"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-10 pr-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
                />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-300 bg-white p-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
              >
                <option value="">Todos os status</option>
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                Origem
              </label>
              <select
                value={patientFilter}
                onChange={(e) => setPatientFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-300 bg-white p-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
              >
                <option value="">Todos os lançamentos</option>
                <option value="external">Somente receitas externas</option>
                {patients.map((patient) => (
                  <option key={patient.id} value={patient.id}>
                    {patient.full_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600">
              <Filter className="mr-2 h-4 w-4 text-purple-600" />
              <span className="font-semibold text-slate-900">
                {filteredInvoices.length}
              </span>
              <span className="ml-1">de {invoices.length}</span>
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearWorkspaceFilters}
                className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
              >
                Limpar filtros
              </button>
            )}
          </div>
        </div>
      </section>

                {/* Invoices List */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="w-10 px-2 py-3"></th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fatura
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Paciente / Origem
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vencimento / Competência
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredInvoices.map((invoice) => {
                        const isInstallment = invoice.payment_type === 'installment';
                        const invoiceInstallments = Array.isArray(invoice.installments) ? invoice.installments : [];
                        return (
                          <React.Fragment key={invoice.id}>
                          <tr className="hover:bg-gray-50">
                            <td className="px-2 py-4">
                              {isInstallment && (
                                <button
                                  onClick={() => setExpandedInvoices((prev) => ({
                                    ...prev,
                                    [invoice.id]: !prev[invoice.id]
                                  }))}
                                  className="rounded p-1 text-gray-600 hover:bg-gray-100"
                                >
                                  {expandedInvoices[invoice.id]
                                    ? <ChevronDown className="w-4 h-4" />
                                    : <ChevronRight className="w-4 h-4" />}
                                </button>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div>
                                <div className="font-medium text-gray-900">
                                  #{invoice.invoice_number}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {formatDate(invoice.issue_date)}
                                </div>
                                {isInstallment && (
                                  <div className="text-xs font-medium text-purple-700 mt-1">
                                    {invoice.installment_count} parcelas
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900">
                                {invoice.invoice_type === 'non_patient'
                                  ? invoice.partner_name || 'Receita externa'
                                  : invoice.patient_name}
                              </div>
                              {invoice.invoice_type === 'non_patient' && (
                                <div className="text-xs text-indigo-700 mt-1">
                                  {getExternalServiceLabel(invoice)}
                                </div>
                              )}
                              {invoice.invoice_origin === 'appointment_completion' && (
                                <div className="mt-1 text-xs font-medium text-emerald-700">
                                  Criada ao finalizar o atendimento
                                  {invoice.created_by_name ? ` por ${invoice.created_by_name}` : ''}
                                </div>
                              )}
                              {invoice.invoice_origin === 'manual' && invoice.invoice_type !== 'non_patient' && (
                                <div className="mt-1 text-xs text-gray-500">Lançamento manual</div>
                              )}
                              {invoice.invoice_type !== 'non_patient' && Number(patientWalletSummaries[invoice.patient_id]?.available_balance || 0) > 0 && (
                                <div className="text-xs text-green-700 font-medium mt-1">
                                  Crédito disponível:{' '}
                                  {formatCurrency(patientWalletSummaries[invoice.patient_id]?.available_balance || 0)}
                                </div>
                              )}                              
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900">
                                {formatCurrency(invoice.total_amount)}
                              </div>
                              {invoice.subtotal !== invoice.total_amount && (
                                <div className="text-xs text-gray-500">
                                  Subtotal: {formatCurrency(invoice.subtotal)}
                                </div>
                              )}
                              {/* Status base da fatura */}
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {invoice.invoice_type === 'non_patient'
                                  ? invoice.competence?.split('-').reverse().join('/') || '—'
                                  : formatDate(invoice.due_date)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                  invoice.status
                                )}`}
                              >
                                {statusOptions.find((s) => s.value === invoice.status)?.label ||
                                  invoice.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex flex-wrap items-center justify-end gap-2">
                                {/* Badge de NFS-e */}
                                {invoice.nfse_status === 'emitida' && (
                                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200" title={`Chave: ${invoice.nfse_chave_acesso || 'N/A'}`}>
                                    <Receipt className="w-3 h-3 mr-1" />
                                    NFS-e {invoice.nfse_numero ? `#${invoice.nfse_numero}` : 'Vinculada'}
                                  </span>
                                )}

                                {/* Editar fatura */}
                                <button
                                  onClick={() =>
                                    invoice.invoice_type === 'non_patient'
                                      ? handleEditExternalRevenue(invoice)
                                      : handleEditInvoice(invoice)
                                  }
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100"
                                >
                                  <Edit className="w-3 h-3 mr-1" />
                                  Editar
                                </button>

                                {invoice.invoice_type === 'non_patient' && (
                                  <button
                                    onClick={() => handleDuplicateExternalRevenue(invoice)}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                                    title="Criar lançamento para a próxima competência"
                                  >
                                    <Copy className="w-3 h-3 mr-1" />
                                    Duplicar
                                  </button>
                                )}

                                {/* Registrar pagamento apenas para faturas de pacientes */}
                                {invoice.invoice_type !== 'non_patient' && !isInstallment && (
                                  <button
                                    onClick={() => handleAddPayment(invoice)}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-lg bg-green-50 text-green-700 hover:bg-green-100"
                                  >
                                    <CreditCard className="w-3 h-3 mr-1" />
                                    Pagamento
                                  </button>
                                )}

                                {/* Dispensar NFS-e */}
                                {invoice.nfse_status !== 'dispensada' && invoice.nfse_status !== 'emitida' && (
                                  <button
                                    onClick={() => handleDispenseNfse(invoice.id)}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-lg bg-orange-50 text-orange-700 hover:bg-orange-100"
                                    title="Marcar como Cortesia/Isenta"
                                  >
                                    <Archive className="w-3 h-3 mr-1" />
                                    Dispensar NFS-e
                                  </button>
                                )}

                                {/* Excluir fatura */}
                                <button
                                  onClick={() => handleDeleteInvoice(invoice.id)}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100"
                                >
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  Excluir fatura
                                </button>
                              </div>
                            </td>
                          </tr>

                          {isInstallment && expandedInvoices[invoice.id] && (
                            <tr>
                              <td colSpan="7" className="bg-purple-50/50 px-5 py-4">
                                <div className="overflow-x-auto rounded-lg border border-purple-100 bg-white">
                                  <table className="min-w-full divide-y divide-gray-100">
                                    <thead className="bg-purple-50">
                                      <tr>
                                        {['Parcela', 'Vencimento', 'Valor', 'Recebido', 'Saldo', 'Status', 'Ação'].map((title) => (
                                          <th key={title} className="px-4 py-2 text-left text-xs font-semibold uppercase text-purple-800">
                                            {title}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                      {invoiceInstallments.map((installment) => {
                                        const balance = Math.max(
                                          Number(installment.amount || 0) - Number(installment.received_amount || 0),
                                          0
                                        );
                                        const labels = {
                                          pending: 'Pendente',
                                          partially_received: 'Parcialmente recebida',
                                          received: 'Recebida',
                                          overdue: 'Vencida',
                                          cancelled: 'Cancelada'
                                        };
                                        const classes = {
                                          pending: 'bg-amber-100 text-amber-800',
                                          partially_received: 'bg-blue-100 text-blue-800',
                                          received: 'bg-green-100 text-green-800',
                                          overdue: 'bg-red-100 text-red-800',
                                          cancelled: 'bg-gray-100 text-gray-700'
                                        };
                                        return (
                                          <tr key={installment.id}>
                                            <td className="px-4 py-2 text-sm font-semibold">
                                              {installment.installment_number}/{installment.total_installments}
                                            </td>
                                            <td className="px-4 py-2 text-sm">{formatDate(installment.due_date)}</td>
                                            <td className="px-4 py-2 text-sm">{formatCurrency(installment.amount)}</td>
                                            <td className="px-4 py-2 text-sm text-green-700">{formatCurrency(installment.received_amount)}</td>
                                            <td className="px-4 py-2 text-sm font-semibold">{formatCurrency(balance)}</td>
                                            <td className="px-4 py-2">
                                              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${classes[installment.status] || 'bg-gray-100 text-gray-700'}`}>
                                                {labels[installment.status] || installment.status}
                                              </span>
                                            </td>
                                            <td className="px-4 py-2">
                                              {balance > 0 && installment.status !== 'cancelled' && (
                                                <button
                                                  onClick={() => handleAddPayment(invoice, installment)}
                                                  className="inline-flex items-center rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700"
                                                >
                                                  <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                                                  Receber
                                                </button>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

    </div>
  );
};

export default FinancialInvoicesWorkspace;
